import type { Article } from "./types";

export const article: Article = {
  slug: "phone-rules-for-christian-families",
  title: "Phone rules for Christian families that actually hold",
  h1: "Phone rules for Christian families",
  description:
    "The rules children keep apply to the adults too, name places and times rather than hours, and were agreed rather than announced. What that looks like in practice.",
  targets: [
    "christian family phone rules",
    "phone boundaries for kids christian",
    "family screen time rules christian",
  ],
  published: "2026-08-26",
  standfirst:
    "Almost every family phone rule fails for the same reason: it is a rule for the children, enforced by adults who are on their phones while they enforce it.",
  takeaways: [
    "A rule that applies to the whole household is kept far better than one aimed at the children, and the reason is not fairness — it is that a rule nobody models is understood as a punishment.",
    "Rules about places and times hold; rules about hours do not, because an hourly allowance is negotiable every single day.",
    "Two boundaries do most of the work: no phones at the table, and no phones in bedrooms overnight — including the adults'.",
    "Monitoring software is a poor substitute for a conversation, and it works badly past about thirteen, when it becomes a puzzle rather than a boundary.",
  ],
  body: [
    {
      type: "p",
      text: "Ask a parent what the phone rule is in their house and you will usually get a number: an hour a day, two at weekends, none until homework is done. Ask a fortnight later how it is going and you will usually get a version of the same answer, which is that it worked for a while. It nearly always works for a while.",
    },
    {
      type: "p",
      text: "Hourly limits fail because they make the phone the subject of a daily negotiation. Every day contains a case for an exception — a long journey, a bad afternoon, a friend's birthday, a parent who needs twenty quiet minutes — and each exception is individually reasonable. Within a month the rule is whatever was agreed most recently, and nobody can remember what the original was.",
    },
    {
      type: "h2",
      id: "places-and-times",
      text: "Rules about places and times, not hours",
    },
    {
      type: "p",
      text: "A boundary that names a place or a time is not negotiable in the same way, because it does not require anybody to assess anything. Nobody has to decide whether this particular dinner warrants an exception; the table is the table. That single property is why these hold and allowances do not.",
    },
    {
      type: "ul",
      items: [
        "No phones at the table. The most valuable of the three, and the only one with a visible effect within a week.",
        "No phones in bedrooms overnight. One charging point, somewhere public, everybody's phone on it. Buy alarm clocks; they cost less than the argument.",
        "No phones for the first thirty minutes of the day. The morning sets the shape of everything after it, and it is the cheapest half-hour to protect.",
        "One afternoon a week with no phones in the house at all — the family version of a digital sabbath, and the one children remember later.",
      ],
    },
    {
      type: "p",
      text: "Three is about the limit. A household with eleven rules has none, because nobody can hold eleven things in mind at six in the evening on a Wednesday. If the fourth appeals, {the-digital-sabbath-piece} sets out the weekly version and the two mistakes that end most attempts inside a fortnight.",
      links: { "the-digital-sabbath-piece": "/blog/digital-sabbath" },
    },
    {
      type: "note",
      text: "If a rule does not apply to the adults, it is not a rule. It is a restriction, and children read the difference immediately and accurately.",
    },
    {
      type: "h2",
      id: "the-adults",
      text: "The part about the adults",
    },
    {
      type: "p",
      text: "This is the uncomfortable half and it is where most family phone policies quietly die. A rule enforced by somebody who is visibly exempt from it does not teach self-control; it teaches that self-control is a thing imposed on people who lack power, and that the reward for growing up is being allowed to stop.",
    },
    {
      type: "p",
      text: "It is also, straightforwardly, harder for the adults. A child asked to leave a phone downstairs is giving up entertainment. An adult doing the same is often giving up work messages, a group chat that carries most of their friendships, and the one reliable way they have of putting the day down. That difficulty is worth saying out loud in front of the children rather than concealing, because it is the actual lesson.",
    },
    {
      type: "verse",
      verse: {
        text: "Train up a child in the way he should go, and when he is old he will not depart from it.",
        ref: "Proverbs 22:6",
      },
    },
    {
      type: "p",
      text: "Training in the older sense is not instruction. It is doing the thing repeatedly alongside someone until the shape of it is theirs. A household where the adults put their phones in a basket at six is training; a household where the adults announce a rule from behind a screen is issuing policy, and policy has never formed anybody.",
    },
    {
      type: "h2",
      id: "agreed-not-announced",
      text: "Agreed rather than announced",
    },
    {
      type: "p",
      text: "For anyone over about eleven, the difference between a rule that was explained and a rule that was imposed shows up within days. That does not mean the rules are up for a vote. It means the reasons are said out loud, the children get to argue, and one thing they argue for actually changes — because a boundary that has never survived a challenge is one they will keep testing to find out whether it is real.",
    },
    {
      type: "ol",
      items: [
        "Sit down once, with everybody, and write down what the household is agreeing to. Three rules, on paper, on the fridge.",
        "Say what each one is for. Not because phones are bad — because dinner is a conversation, and because sleep is the thing everything else runs on.",
        "Agree what happens when it is broken, including by an adult, before it is broken by anybody.",
        "Review it in a month. A rule that is failing is information about the rule, not about the child.",
      ],
    },
    {
      type: "h2",
      id: "monitoring",
      text: "On monitoring software",
    },
    {
      type: "p",
      text: "It has a place, and the place is narrower than the marketing suggests. For a younger child, content filtering is straightforwardly sensible and barely controversial. For a teenager, covert monitoring is a poor trade: it is usually discovered, discovery costs more trust than the information was worth, and the technically minded find a way around it within a fortnight, at which point you have less visibility than when you started and no conversation either.",
    },
    {
      type: "p",
      text: "What ages better is a tool everybody in the house uses, openly, including the parents — a block that applies at agreed times on agreed apps, visible to everyone, discussed rather than installed in secret. That is the model behind Bible Shield: the apps close at the times you set and a short prayer opens them again, and there is nothing hidden about it. {The-longer-argument} is here, along with why a barrier that asks for something works differently from one that just refuses.",
      links: { "The-longer-argument": "/blog/christian-app-blocker-guide" },
    },
    {
      type: "p",
      text: "One caution before you buy anything: work out which pattern you are actually dealing with first. A household where the problem is the last hour of the evening needs something quite different from one where a child reaches for a phone in every unoccupied moment, and {the-phone-and-faith-test} sorts that out in four minutes — for the adults as much as for anybody else.",
      links: { "the-phone-and-faith-test": "/quiz/phone-and-faith-test" },
    },
  ],
  faq: [
    {
      q: "What age should a child get a smartphone?",
      a: "There is no verse and no consensus, and the honest answer is that it depends more on the household's rules than on the birthday. What is well established is that a phone in a bedroom overnight costs sleep at every age — so whatever age you choose, set that boundary on the first day rather than retrofitting it.",
    },
    {
      q: "Should Christian parents monitor their children's phones?",
      a: "Filtering for younger children is uncontroversial. Covert monitoring of teenagers usually costs more in trust than it returns in information, and it is generally discovered. Open, agreed tools that apply to the whole household age far better.",
    },
    {
      q: "How do we handle homework, which is on the same device?",
      a: "This is the strongest argument against hour-based rules, since the device is genuinely required. Place-and-time rules handle it cleanly: work happens where other people are, which solves most of it without anyone having to adjudicate what counts as homework.",
    },
    {
      q: "What if the rules keep collapsing?",
      a: "Usually one of three things: there are too many, they do not apply to the adults, or they were announced rather than agreed. Cut to three, apply them to everybody, and hold one conversation where the children get to argue and one of their arguments actually wins.",
    },
  ],
  related: [
    "digital-sabbath",
    "christian-app-blocker-guide",
    "screen-time-limits-dont-work",
  ],
};
